import React from "react";
import { useTranslation } from "react-i18next";

/**
 * SMS State Badge Component
 * Displays the status of an SMS with a colored badge and dot indicator
 *
 * @param {Object} props
 * @param {string} props.state - The state: "Delivered", "Pending", "Failed" or "NotSent"
 * @param {string} props.size - The size: "Large" or "Medium" (default: "Large")
 * @param {string} props.className - Additional CSS classes
 */

const stateConfigs = {
  delivered: {
    bg: "#E5F7EE",
    text: "#1b5e20",
    dotColor: "#1b5e20",
    labelKey: "common.smsStatus.DELIVERED",
  },
  pending: {
    bg: "#FFF1E6",
    text: "#e65100",
    dotColor: "#e65100",
    labelKey: "common.smsStatus.PENDING",
  },
  failed: {
    bg: "#FCE4EC",
    text: "#ad1457",
    dotColor: "#ad1457",
    labelKey: "common.smsStatus.FAILED",
  },
  // Never handed to EVERY8D: no points, not counted as usage
  notsent: {
    bg: "#F5F5F5",
    text: "#616161",
    dotColor: "#616161",
    labelKey: "common.smsStatus.NOT_SENT",
  },
};

const SmsState = ({ state = "Delivered", size = "Large", className = "" }) => {
  const { t } = useTranslation();
  // Normalize state to lowercase for matching
  const normalizedState = (state || "").toLowerCase();

  // Handle different state values (success/delivered)
  let stateKey = normalizedState;
  if (normalizedState === "success") {
    stateKey = "delivered";
  }

  const config = stateConfigs[stateKey] || stateConfigs.failed;

  // Size configurations
  const sizeConfig = {
    Large: {
      fontSize: "16px",
      padding: "4px 8px",
    },
    Medium: {
      fontSize: "12px",
      padding: "2px 6px",
    },
  };

  const currentSize = sizeConfig[size] || sizeConfig.Large;

  const badgeStyle = {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    padding: currentSize.padding,
    borderRadius: "4px",
    backgroundColor: config.bg,
    color: config.text,
    fontSize: currentSize.fontSize,
    fontWeight: 600,
    fontFamily: "Poppins, sans-serif",
    lineHeight: 1.2,
  };

  const dotStyle = {
    width: "6px",
    height: "6px",
    borderRadius: "50%",
    backgroundColor: config.dotColor,
    flexShrink: 0,
  };

  return (
    <span
      className={`sms-state sms-state-${stateKey} ${className}`}
      style={badgeStyle}
    >
      <span style={dotStyle} />
      {t(config.labelKey)}
    </span>
  );
};

export default SmsState;
